import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

const AuditFocusContext = createContext(null);

const STORAGE_KEY = 'komplystack:audit_focus';

function parseFocusParam(raw) {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function toFocusParam(slugs) {
  return (slugs || []).filter(Boolean).join(',');
}

export function AuditFocusProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [adoptedStandards, setAdoptedStandards] = useState([]);
  const [activeAudit, setActiveAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  const urlFocus = parseFocusParam(searchParams.get('audit_focus'));

  const [focusSlugs, setFocusSlugsState] = useState(() => {
    if (urlFocus.length > 0) return urlFocus;
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await apiService.getCompanyCertifications();
        const data = res?.data || res;
        if (cancelled) return;
        setAdoptedStandards(data.adopted_standards || []);
        setActiveAudit(data.active_audit || null);

        if (urlFocus.length === 0 && focusSlugs.length === 0 && data.active_audit?.standards?.length) {
          setFocusSlugsState(data.active_audit.standards);
        }
      } catch {
        if (!cancelled) {
          setAdoptedStandards([]);
          setActiveAudit(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFocusSlugs = useCallback((slugs) => {
    const next = (slugs || []).filter(Boolean);
    setFocusSlugsState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams(searchParams);
    if (next.length > 0) params.set('audit_focus', toFocusParam(next));
    else params.delete('audit_focus');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const toggleFocus = useCallback((slug) => {
    setFocusSlugsState((prev) => {
      const has = prev.includes(slug);
      const next = has ? prev.filter((s) => s !== slug) : [...prev, slug];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      const params = new URLSearchParams(searchParams);
      if (next.length > 0) params.set('audit_focus', toFocusParam(next));
      else params.delete('audit_focus');
      setSearchParams(params, { replace: true });
      return next;
    });
  }, [searchParams, setSearchParams]);

  const clearFocus = useCallback(() => setFocusSlugs([]), [setFocusSlugs]);

  const value = useMemo(() => ({
    loading,
    adoptedStandards,
    activeAudit,
    focusSlugs,
    hasFocus: focusSlugs.length > 0,
    setFocusSlugs,
    toggleFocus,
    clearFocus,
    auditFocusQuery: toFocusParam(focusSlugs),
  }), [loading, adoptedStandards, activeAudit, focusSlugs, setFocusSlugs, toggleFocus, clearFocus]);

  return (
    <AuditFocusContext.Provider value={value}>
      {children}
    </AuditFocusContext.Provider>
  );
}

export function useAuditFocus() {
  const ctx = useContext(AuditFocusContext);
  if (!ctx) {
    return {
      loading: false,
      adoptedStandards: [],
      activeAudit: null,
      focusSlugs: [],
      hasFocus: false,
      setFocusSlugs: () => {},
      toggleFocus: () => {},
      clearFocus: () => {},
      auditFocusQuery: '',
    };
  }
  return ctx;
}
